import React, { ReactElement, ReactNode } from 'react';
import classNames from 'classnames';
import './floatingHeader.scss';
import { Brands } from '@lmig/lmds-helpers/dist/types/brands';
import { GridRow, GridCol } from '@lmig/lmds-react-grid';
import { Breakpoints } from '@lmig/lmds-helpers/dist/types/breakpoints';
import { ButtonOrLink } from '@lmig/lmds-react-button-or-link';
import { BrandLinkProps, FloatingHeaderBaseProps } from './types';
import { LOGOS } from './constants';
import { HeaderAppName } from './components/HeaderAppName';
import { FloatingHeaderContext } from './context/FloatingHeaderContext';
import { useActiveMenu } from './hooks/useActiveMenu';
import { useMinBreakpoint } from './hooks/useMinBreakpoint';
import { HeaderNavigationContext } from './context/HeaderNavigationContext';
import { LogoWrapper } from './components/LogoWrapper';

export interface FloatingHeaderProps extends FloatingHeaderBaseProps {
  /** Header content is rendered inside `<header class="lmig-FloatingHeader">`. */
  children?: React.ReactNode;
  /**
   * If an edge-to-edge, gridless header is desired, set this to true.
   * @default false
   */
  isFullWidth?: boolean;
  /** Determines whether the header should be sticky when scrolling down a page.
   * @default false
   */
  isSticky?: boolean;
  /** Determines whether the header background spans the entire width of the header.
   * @default false
   */
  isFullBackground?: boolean;
  /** Determines logo and background color.
   * @default 'liberty'
   */
  brand?: Brands;
  /**
   * Props used to create a clickable logo link.
   */
  brandLinkProps?: BrandLinkProps;
  /**
   * Application name to display next to the brand logo in the `FloatingHeader`.
   */
  appName?: ReactNode;
  /** Breakpoints at which the header navigation transforms into the an overlay menu.
   * @default ['base']
   */
  panelFromLinkBreakpoint?: Breakpoints | null;
  /** At or above this breakpoint, the menu switches from vertical to horizontal. If this breakpoint is null, the vertical menu will always be used. If this breakpoint is 'base', the horizontal menu will always be used.
   * @default false
   */
  overlayIsOpen?: boolean;
}

/**
 * @see [LMDS FloatingHeader Documentation and Examples](https://designsystem.lmig.com/experimental/floating-header/code)
 *
 * API:
 *
 * @implements [FloatingHeaderProps](https://designsystem.lmig.com/experimental/floating-header/code#floating-header-props)
 * @extends [HTMLDivElement](https://microsoft.github.io/PowerBI-JavaScript/interfaces/_node_modules_typedoc_node_modules_typescript_lib_lib_dom_d_.htmldivelement.html)
 */
export const FloatingHeader = (props: FloatingHeaderProps): ReactElement => {
  const {
    id,
    className,
    children,
    isFullWidth = false,
    isSticky = false,
    brand = 'liberty',
    isFullBackground = false,
    appName,
    overlayIsOpen = false,
    brandLinkProps,
    ...otherProps
  } = props;

  // This hook is used within HeaderNavigation to ensure only one menu is open within the 'desktop view'
  const { activeMenuID, setActiveMenuID } = useActiveMenu();
  const showHorizontalMenu = useMinBreakpoint('custom');

  const singleBrandClass = `lmig-FloatingHeader-brand--${brand}`;

  const containerClasses = classNames('lmig-FloatingHeader', className, `lmig-FloatingHeader--${brand}`, {
    'lmig-FloatingHeader--isSticky': isSticky,
    'lmig-FloatingHeader--isFullWidth': isFullWidth,
    'lmig-FloatingHeader--bg-full': isFullBackground,
  });
  // lmig-FloatingHeader-inner--${brand} added to modify link color for now.
  const innerClasses = classNames(
    'lmig-FloatingHeader-inner',
    `lmig-FloatingHeader-inner--${brand}`,
    {
      [`lmig-FloatingHeader-inner--bg-full--${brand}`]: isFullBackground,
    },
    /** This applies modifier to inner div to apply border radius if the overlayNav menu is open or a HeaderParentItem is expanded. */
    {
      'lmig-FloatingHeader-inner--overlayIsOpen': (overlayIsOpen && !showHorizontalMenu) || activeMenuID !== '',
    },
  );

  const brandClasses = classNames('lmig-FloatingHeader-brand', { [singleBrandClass]: !isFullBackground });
  const logoClasses = classNames('lmig-FloatingHeader-brand-logo', `lmig-FloatingHeader-brand-logo--${brand}`);

  const DisplayLogo = LOGOS[brand];
  const LogoContainer = brandLinkProps ? ButtonOrLink : LogoWrapper;
  const logoContainerProps = brandLinkProps ? { ...brandLinkProps } : {};

  const innerFloatingHeader = (
    <div className={innerClasses}>
      <LogoContainer {...logoContainerProps} className={brandClasses}>
        <DisplayLogo className={logoClasses} />
      </LogoContainer>
      {appName && <HeaderAppName hidePipe={!isFullBackground}>{appName}</HeaderAppName>}
      {children}
    </div>
  );

  return (
    <FloatingHeaderContext.Provider value={{ overlayIsOpen, showHorizontalMenu }}>
      <HeaderNavigationContext.Provider value={{ activeMenuID, setActiveMenuID }}>
        <header id={id} className={containerClasses} {...otherProps}>
          {isFullWidth ? (
            innerFloatingHeader
          ) : (
            <GridRow mainGrid={!isFullWidth}>
              <GridCol base={12}>{innerFloatingHeader}</GridCol>
            </GridRow>
          )}
        </header>
      </HeaderNavigationContext.Provider>
    </FloatingHeaderContext.Provider>
  );
};
